import { PlatformIconComponent } from "@/biz/platform";
import { Tooltip } from "antd";
import { SameInfluencerIcon, StarBgIcon } from "../icons";
import { formatNumber } from "@/biz/tool";
import { useTranslation } from "react-i18next";
import { Tag } from "aha-components";
import { AdsCampaignMatchingAccuracy } from "@/pages/campaignCreate/components/settings/config";
import MacthFromTag from "../MacthFromTag";
import { isFeishuEnv } from "@/utils/feishu";
import { s3ImageLink } from "@/utils/links";

export default function TableCreatorName({
  handleClick,
  item,
  showFollowers = false,
  listShowFollowers = false,
  showCreatorPersonaType = true,
}: {
  handleClick?: Function;
  item: any;
  showFollowers?: boolean;
  listShowFollowers?: boolean;
  showCreatorPersonaType?: boolean;
}) {
  const { t } = useTranslation();
  const userName = item?.userName?.startsWith("@")
    ? item.userName.slice(1)
    : item?.userName;
  const creatorNameMaxWidthClass = isFeishuEnv()
    ? "max-w-[120px]"
    : "max-w-[140px] md:max-w-[160px]";

  // 将 matchingAccuracy 枚举值转换为本地化文本
  const getMatchingAccuracyLabel = (accuracy: string) => {
    const matchText = t("components.tableCreatorName.match", {
      defaultValue: "match",
    });
    if (accuracy === AdsCampaignMatchingAccuracy.FOCUS_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.focusMode.label", {
          defaultValue: "Focus mode",
        }) +
        " " +
        matchText
      );
    }
    if (accuracy === AdsCampaignMatchingAccuracy.SCALE_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.scaleMode.label", {
          defaultValue: "Scale mode",
        }) +
        " " +
        matchText
      );
    }
    if (accuracy === AdsCampaignMatchingAccuracy.MAX_SCALE_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.maxScaleMode.label", {
          defaultValue: "Max scale mode",
        }) +
        " " +
        matchText
      );
    }
    return accuracy;
  };

  return (
    <div
      className="flex items-center"
      onClick={() => handleClick && handleClick(item)}
    >
      <div className="cursor-pointer mr-3">
        <div
          className={`${listShowFollowers ? "w-14 h-14" : "w-12 h-12"} justify-start items-center gap-3 flex relative flex-shrink-0`}
        >
          <img
            alt=""
            className={`${listShowFollowers ? "w-14 h-14" : "w-12 h-12"} rounded-full flex-shrink-0`}
            src={item?.creatorProfilePictureLink || item?.avatarUrl}
          />
          <div className="absolute right-0 bottom-0">
            <PlatformIconComponent
              platform={item?.platform}
              className="w-[20px] h-[20px]"
            />
          </div>
          {(item?.isFromWishlist || item?.isCoreMatch) && (
            <div className="w-[calc(100%+26px)] h-[calc(100%+26px)] absolute top-[-13px] left-[-13px] z-[-1] rounded-full">
              <img
                src={`${s3ImageLink}${item?.isFromWishlist ? "wishlist-56.png" : "aha-picks-56.png"}`}
                alt=""
                className="w-full h-full object-cover flex-shrink-0"
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <div className="text-zinc-950 font-semibold cursor-pointer line-clamp-1 flex items-center gap-1">
            <Tooltip title={item?.creatorName}>
              <div
                className={`${creatorNameMaxWidthClass} overflow-hidden text-ellipsis whitespace-nowrap`}
              >
                {item?.creatorName}
              </div>
            </Tooltip>
            {item?.creatorMixedScore && item?.creatorDeliveryCount >= 4 && (
              <div className="border border-[#FFD1A9] bg-[#FFF6ED] w-[47px] h-[20px] flex items-center justify-center rounded-md gap-1">
                <StarBgIcon className="text-primary w-[14px] h-[14px]" />
                <div className="justify-center text-primary text-[12px] font-semibold font-['DM_Sans']">
                  {item?.creatorMixedScore}
                </div>
              </div>
            )}
          </div>
          {item?.creatorReferenceFrom && (
            <Tooltip
              color="#000"
              title={
                <div>
                  {t(
                    "components.tableCreatorName.influencerMatchedFromReference",
                  )}{" "}
                  <a
                    className="text-link"
                    href={item?.creatorReferenceFrom}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item?.creatorReferenceFrom}
                  </a>
                </div>
              }
            >
              <span>
                <SameInfluencerIcon className="text-[#009966] flex-shrink-0" />
              </span>
            </Tooltip>
          )}
        </div>
        <div
          className={`flex items-center ${listShowFollowers ? "gap-2" : "gap-6"}`}
        >
          <Tooltip title={`@${userName}`}>
            <a
              className={`${listShowFollowers ? "!max-w-[80px] min-w-0 truncate" : "max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap flex-shrink-0"} creator-list-table-content-link inline-block text-xs font-medium !text-[#2B7FFF]`}
              href={item?.creatorProfileLink}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              @{userName}
            </a>
          </Tooltip>
          {showFollowers && (
            <>
              {item?.followers && (
                <div className="inline-flex justify-start items-center gap-1">
                  <div className="justify-start text-Zinc-950 text-xs font-medium font-['DM_Sans'] leading-none">
                    {formatNumber(item?.followers)}
                  </div>
                  <div className="justify-start text-font&icon-secondary text-xs font-normal font-['DM_Sans'] leading-none">
                    {t("components.tableCreatorName.following")}
                  </div>
                </div>
              )}
              {item?.estimateView && (
                <div className="inline-flex justify-start items-center gap-1">
                  <div className="justify-start text-Zinc-950 text-xs font-medium font-['DM_Sans'] leading-none">
                    {formatNumber(item?.estimateView)}
                  </div>
                  <div className="justify-start text-font&icon-secondary text-xs font-normal font-['DM_Sans'] leading-none">
                    {t("components.tableCreatorName.estimatedViewsPost")}
                  </div>
                </div>
              )}
              {item?.matchingAccuracy && (
                <Tag
                  color={
                    item?.matchingAccuracy ===
                    AdsCampaignMatchingAccuracy.FOCUS_MODE
                      ? "#8E51FF"
                      : item?.matchingAccuracy ===
                          AdsCampaignMatchingAccuracy.SCALE_MODE
                        ? "#FE9A00"
                        : "#00B8DB"
                  }
                  variant="fill"
                  className="whitespace-nowrap"
                >
                  {getMatchingAccuracyLabel(item?.matchingAccuracy)}
                </Tag>
              )}
            </>
          )}
          {listShowFollowers && item?.followers ? (
            <>
              <div className="w-[1px] h-[16px] bg-borderColor flex-shrink-0"></div>
              <div className="whitespace-nowrap flex-shrink-0">
                <span className="text-[#79716B]">
                  {t("components.tableCreatorName.followers")}
                </span>
                <span className="text-[#1C1917]">
                  {formatNumber(item?.followers)}
                </span>
              </div>
            </>
          ) : null}
        </div>
        {item?.creatorPersonaType && showCreatorPersonaType && (
          <div>
            <span className="text-[#79716B]">
              {t("components.tableCreatorName.influencerProfile", {
                defaultValue: "Influencer profile: ",
              })}
            </span>
            <span className="text-[#1C1917]">{item?.creatorPersonaType}</span>
          </div>
        )}
        {item?.productMatchedFrom && (
          <MacthFromTag
            className="!w-[200px]"
            brandName={item?.productMatchedFrom}
            tooltipText={t("components.matchFromTag.matchedFrom", {
              defaultValue: "Matched from {brandName}",
              brandName: item?.productMatchedFrom,
            })}
          />
        )}
      </div>
    </div>
  );
}
